import { supabase } from './supabaseClient';

export interface ExplorerLensFocus {
  id: string;
  user_id: string;
  song_id: string;
  lens_category: string;
  archetype_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Get the active lens focus for a user and song
 */
export async function getLensFocus(userId: string, songId: string): Promise<ExplorerLensFocus | null> {
  const { data, error } = await supabase
    .from('explorer_lens_focus')
    .select('*')
    .eq('user_id', userId)
    .eq('song_id', songId)
    .maybeSingle();

  if (error) {
    console.error('[ExplorerLensFocus] Error fetching lens focus:', error);
    return null;
  }

  return data;
}

/**
 * Set or update the active lens focus for a user and song
 */
export async function setLensFocus(
  userId: string,
  songId: string,
  lensCategory: string,
  archetypeId: string
): Promise<ExplorerLensFocus | null> {
  const { data, error } = await supabase
    .from('explorer_lens_focus')
    .upsert({
      user_id: userId,
      song_id: songId,
      lens_category: lensCategory,
      archetype_id: archetypeId,
    }, {
      onConflict: 'user_id,song_id',
    })
    .select()
    .single();

  if (error) {
    console.error('[ExplorerLensFocus] Error setting lens focus:', error);
    return null;
  }

  return data;
}

/**
 * Clear the active lens focus for a user and song
 */
export async function clearLensFocus(userId: string, songId: string): Promise<boolean> {
  const { error } = await supabase
    .from('explorer_lens_focus')
    .delete()
    .eq('user_id', userId)
    .eq('song_id', songId);

  if (error) {
    console.error('[ExplorerLensFocus] Error clearing lens focus:', error);
    return false;
  }

  return true;
}

/**
 * Get all lens focuses for a user (for analytics or cleanup)
 */
export async function getUserLensFocuses(userId: string): Promise<ExplorerLensFocus[]> {
  const { data, error } = await supabase
    .from('explorer_lens_focus')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('[ExplorerLensFocus] Error fetching user lens focuses:', error);
    return [];
  }

  return data || [];
}
